import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle2,
  X,
  FileText,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import { Modal } from '../common/Modal.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { Project } from '../../../shared/types.ts';

interface ImportTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  projects?: Project[];
  onTasksImported?: (count: number) => void;
}

interface ParsedTaskRow {
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  assigneeEmail?: string;
  dueDate?: string;
  tags?: string[];
  estimatedHours?: number;
  isValid: boolean;
  error?: string;
}

// Rendering tens of thousands of rows freezes the browser, so only the first rows are previewed
const PREVIEW_ROW_LIMIT = 200;

export const ImportTasksModal: React.FC<ImportTasksModalProps> = ({
  isOpen,
  onClose,
  projectId: initialProjectId,
  projects = [],
  onTasksImported,
}) => {
  const { token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    initialProjectId || (projects[0]?.id || '')
  );
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedTaskRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Sync initial project ID if passed
  React.useEffect(() => {
    if (initialProjectId) {
      setSelectedProjectId(initialProjectId);
    } else if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [initialProjectId, projects]);

  const handleDownloadTemplate = (type: 'xlsx' | 'csv' = 'xlsx') => {
    const headers = [
      'Title',
      'Description',
      'Status',
      'Priority',
      'Assignee Email',
      'Due Date (YYYY-MM-DD)',
      'Estimated Hours',
      'Tags',
    ];

    const sampleRows = [
      [
        'Build User Authentication & Session Security',
        'Implement OAuth2 and session token refresh logic',
        'todo',
        'urgent',
        'mohiuddin@ihbdigital.com',
        new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
        8,
        'backend, security',
      ],
      [
        'Design Sprint Kanban Board UI Component',
        'Include drag-and-drop animations and swimlane filters',
        'in_progress',
        'high',
        'shayan@ihbdigital.com',
        new Date(Date.now() + 86400000 * 5).toISOString().split('T')[0],
        12,
        'frontend, ui',
      ],
      [
        'Deploy PostgreSQL Database to Production',
        'Verify connection pool and execute schema migrations',
        'review',
        'medium',
        'abdul@ihbdigital.com',
        new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0],
        4,
        'devops, database',
      ],
    ];

    const data = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Auto-adjust column widths
    ws['!cols'] = [
      { wch: 35 }, // Title
      { wch: 45 }, // Description
      { wch: 15 }, // Status
      { wch: 15 }, // Priority
      { wch: 28 }, // Assignee Email
      { wch: 22 }, // Due Date
      { wch: 18 }, // Estimated Hours
      { wch: 25 }, // Tags
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');

    if (type === 'csv') {
      XLSX.writeFile(wb, 'ihb_pulse_task_import_template.csv', { bookType: 'csv' });
    } else {
      XLSX.writeFile(wb, 'ihb_pulse_task_import_template.xlsx', { bookType: 'xlsx' });
    }
  };

  const processFile = async (uploadedFile: File) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setIsParsing(true);
    setFile(uploadedFile);

    try {
      const buffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];

      if (!firstSheetName) {
        setErrorMsg('The spreadsheet contains no readable sheets.');
        setIsParsing(false);
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      // Real-world sheets often have a title block above the header, so look for the header
      // row (one that has a task title column) within the first 15 rows.
      const TITLE_HEADERS = ['title', 'task name', 'task', 'name', 'summary'];
      const sheetRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      let headerRow = sheetRange.s.r;
      for (let r = sheetRange.s.r; r <= Math.min(sheetRange.e.r, sheetRange.s.r + 14); r++) {
        let found = false;
        for (let c = sheetRange.s.c; c <= sheetRange.e.c && !found; c++) {
          const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
          if (cell && TITLE_HEADERS.includes(String(cell.v).trim().toLowerCase())) found = true;
        }
        if (found) {
          headerRow = r;
          break;
        }
      }
      const rawData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
        defval: '',
        range: headerRow,
      });

      const hasTitleColumn = rawData.length > 0 && Object.keys(rawData[0]).some((k) => TITLE_HEADERS.includes(k.trim().toLowerCase()));
      if (!hasTitleColumn) {
        setErrorMsg(
          'No task title column found. The first row of the sheet needs a header such as "Title" or "Task Name" (see the template).'
        );
        setIsParsing(false);
        return;
      }

      if (rawData.length === 0) {
        setErrorMsg('The spreadsheet is empty. Please check your data and retry.');
        setIsParsing(false);
        return;
      }

      const parsed: ParsedTaskRow[] = [];

      for (let i = 0; i < rawData.length; i++) {
        const row = rawData[i];

        // Normalize keys by trimming and lowercasing
        const normalizedRow: Record<string, any> = {};
        for (const [k, v] of Object.entries(row)) {
          normalizedRow[k.trim().toLowerCase()] = v;
        }

        // Title resolution
        const title =
          normalizedRow['title'] ||
          normalizedRow['task name'] ||
          normalizedRow['task'] ||
          normalizedRow['name'] ||
          normalizedRow['summary'] ||
          '';

        const cleanTitle = String(title).trim();

        if (!cleanTitle) {
          parsed.push({
            title: '(Empty Title)',
            isValid: false,
            error: 'Missing task title',
          });
          continue;
        }

        // Description
        const description =
          normalizedRow['description'] ||
          normalizedRow['desc'] ||
          normalizedRow['details'] ||
          normalizedRow['notes'] ||
          '';

        // Status
        const status =
          normalizedRow['status'] ||
          normalizedRow['state'] ||
          'todo';

        // Priority
        const priority =
          normalizedRow['priority'] ||
          normalizedRow['urgency'] ||
          'medium';

        // Assignee
        const assigneeEmail =
          normalizedRow['assignee email'] ||
          normalizedRow['assignee'] ||
          normalizedRow['email'] ||
          normalizedRow['assigned to'] ||
          '';

        // Due date
        let dueDate =
          normalizedRow['due date (yyyy-mm-dd)'] ||
          normalizedRow['due date'] ||
          normalizedRow['deadline'] ||
          normalizedRow['due'] ||
          '';

        if (dueDate) {
          // If Excel serial number date
          if (typeof dueDate === 'number') {
            const excelDate = new Date((dueDate - (25567 + 2)) * 86400 * 1000);
            if (!isNaN(excelDate.getTime())) {
              dueDate = excelDate.toISOString().split('T')[0];
            }
          } else {
            const parsedD = new Date(String(dueDate));
            if (!isNaN(parsedD.getTime())) {
              dueDate = parsedD.toISOString().split('T')[0];
            }
          }
        }

        // Estimated hours
        const estimatedHours =
          Number(normalizedRow['estimated hours'] || normalizedRow['hours'] || normalizedRow['estimate']) || 0;

        // Tags
        const rawTags =
          normalizedRow['tags'] ||
          normalizedRow['labels'] ||
          normalizedRow['tag'] ||
          '';
        const tags = String(rawTags)
          .split(/[,;|]/)
          .map((t) => t.trim())
          .filter(Boolean);

        parsed.push({
          title: cleanTitle,
          description: String(description).trim(),
          status: String(status).trim(),
          priority: String(priority).trim(),
          assigneeEmail: String(assigneeEmail).trim(),
          dueDate: dueDate ? String(dueDate).trim() : undefined,
          tags,
          estimatedHours,
          isValid: true,
        });
      }

      setParsedRows(parsed);
    } catch (err: any) {
      console.error('Error reading spreadsheet:', err);
      setErrorMsg('Failed to parse spreadsheet file: ' + err.message);
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedRows([]);
    setErrorMsg(null);
    setSuccessMsg(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedProjectId) {
      setErrorMsg('Please select a target project.');
      return;
    }

    const validRows = parsedRows.filter((r) => r.isValid);
    if (validRows.length === 0) {
      setErrorMsg('No valid tasks to import.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      // Big spreadsheets are sent in batches so no single request gets too large
      const BATCH_SIZE = 500;
      let importedCount = 0;
      const batchErrors: string[] = [];

      for (let start = 0; start < validRows.length; start += BATCH_SIZE) {
        const res = await fetch('/api/tasks/import', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            projectId: selectedProjectId,
            tasks: validRows.slice(start, start + BATCH_SIZE),
          }),
        });

        // A proxy or size-limit error may not be JSON
        const data = await res.json().catch(() => null);

        if (res.ok && data?.success) {
          importedCount += data.importedCount || 0;
          if (Array.isArray(data.errors)) batchErrors.push(...data.errors);
        } else if (res.status === 413) {
          throw new Error('The import is too large for the server. Split the spreadsheet into smaller files.');
        } else {
          batchErrors.push(
            data?.error ||
              (data?.errors && data.errors.join(', ')) ||
              `Server returned ${res.status} ${res.statusText}`.trim()
          );
          break;
        }
      }

      if (importedCount > 0) {
        setSuccessMsg(`Successfully imported ${importedCount} tasks into project!`);
        if (onTasksImported) {
          onTasksImported(importedCount);
        }
        setTimeout(() => {
          handleReset();
          onClose();
        }, 1200);
      } else {
        setErrorMsg(batchErrors.join(', ') || 'Failed to import tasks.');
      }
    } catch (err: any) {
      console.error('Error importing tasks:', err);
      setErrorMsg('Error communicating with server: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validCount = parsedRows.filter((r) => r.isValid).length;
  const invalidCount = parsedRows.filter((r) => !r.isValid).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Tasks from Excel / CSV"
      maxWidth="max-w-3xl"
    >
      <div className="space-y-4 text-xs font-sans">
        {/* Banner with Neo gradient */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-brand-50 via-white to-brand-50/30 dark:from-surface-850 dark:via-surface-900 dark:to-surface-850 border border-brand-200/60 dark:border-surface-700 flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <FileSpreadsheet className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0" />
              <h4 className="font-bold text-sm text-surface-900 dark:text-surface-100">
                Bulk Task Spreadsheet Import
              </h4>
            </div>
            <p className="text-surface-600 dark:text-surface-300 text-[11px] leading-relaxed">
              Upload spreadsheets (.xlsx, .xls, .csv) to generate Kanban cards and List items automatically.
            </p>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              type="button"
              onClick={() => handleDownloadTemplate('xlsx')}
              className="px-3 py-1.5 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-200 hover:bg-surface-50 font-bold flex items-center space-x-1.5 shadow-sm transition-all"
              title="Download Excel template with sample columns"
            >
              <Download className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
              <span>Download Excel Template</span>
            </button>
          </div>
        </div>

        {/* Project Selector & Documentation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block font-bold text-surface-700 dark:text-surface-300 mb-1">
              Target Project *
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 font-semibold focus:outline-none"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.key})
                </option>
              ))}
            </select>
          </div>

          <div className="p-3 bg-surface-50 dark:bg-surface-800/60 rounded-xl border border-surface-200/60 dark:border-surface-700/60">
            <span className="font-bold text-[11px] text-surface-700 dark:text-surface-300 block mb-1">
              📋 Column Reference Guide
            </span>
            <ul className="text-[10px] space-y-0.5 text-surface-500 dark:text-surface-400">
              <li>
                <span className="font-bold text-brand-600 dark:text-brand-400">Title:</span> Required task title.
              </li>
              <li>
                <span className="font-bold text-surface-700 dark:text-surface-300">Status:</span> todo, in_progress, review, done.
              </li>
              <li>
                <span className="font-bold text-surface-700 dark:text-surface-300">Priority:</span> low, medium, high, urgent.
              </li>
              <li>
                <span className="font-bold text-surface-700 dark:text-surface-300">Assignee:</span> Matches user by company email.
              </li>
            </ul>
          </div>
        </div>

        {/* File Dropzone */}
        {!file ? (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
              isDragOver
                ? 'border-brand-500 bg-brand-50/30 dark:bg-brand-950/20'
                : 'border-surface-300 dark:border-surface-700 hover:border-brand-500 bg-surface-50/50 dark:bg-surface-800/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Upload className="w-8 h-8 text-brand-600 dark:text-brand-400 mx-auto mb-2" />
            <p className="font-bold text-sm text-surface-900 dark:text-surface-100">
              Click to select or drag and drop your spreadsheet here
            </p>
            <p className="text-[11px] text-surface-400 mt-1">
              Supports Microsoft Excel (.xlsx, .xls) and CSV (.csv) files
            </p>
          </div>
        ) : (
          /* File Uploaded Preview Header */
          <div className="p-3 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 flex items-center justify-between">
            <div className="flex items-center space-x-2.5 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-surface-900 dark:text-surface-100 truncate block">
                  {file.name}
                </span>
                <span className="text-[10px] text-surface-400">
                  {(file.size / 1024).toFixed(1)} KB • {validCount} valid tasks ready to import
                  {invalidCount > 0 && ` • ${invalidCount} invalid`}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleReset}
              className="p-1 text-surface-400 hover:text-surface-600 rounded-lg"
              title="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Live Parsed Preview Table */}
        {parsedRows.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-surface-700 dark:text-surface-300">
                Preview Data ({parsedRows.length} tasks parsed)
              </span>
              <div className="flex items-center space-x-2 text-[11px]">
                <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                  ✓ {validCount} Valid
                </span>
                {invalidCount > 0 && (
                  <span className="text-rose-600 dark:text-rose-400 font-bold">
                    ✕ {invalidCount} Invalid
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto rounded-2xl border border-surface-200 dark:border-surface-700">
              <table className="w-full text-left border-collapse text-[11px]">
                <thead className="bg-surface-100 dark:bg-surface-800 sticky top-0 font-bold text-surface-600 dark:text-surface-300">
                  <tr>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">#</th>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">Task Title</th>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">Status</th>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">Priority</th>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">Assignee</th>
                    <th className="p-2 border-b border-surface-200 dark:border-surface-700">Due Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                  {parsedRows.slice(0, PREVIEW_ROW_LIMIT).map((row, idx) => (
                    <tr
                      key={idx}
                      className={
                        !row.isValid
                          ? 'bg-rose-50/40 dark:bg-rose-950/20 text-rose-900 dark:text-rose-200'
                          : 'hover:bg-surface-50 dark:hover:bg-surface-800/50'
                      }
                    >
                      <td className="p-2 font-mono text-[10px] text-surface-400">{idx + 1}</td>
                      <td className="p-2 font-semibold">
                        {row.title}
                        {row.error && (
                          <span className="block text-[10px] text-rose-500">{row.error}</span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 font-mono text-[10px]">
                          {row.status || 'todo'}
                        </span>
                      </td>
                      <td className="p-2">
                        <span
                          className={`px-1.5 py-0.5 rounded font-mono text-[10px] font-bold ${
                            row.priority === 'urgent'
                              ? 'bg-rose-500/10 text-rose-600'
                              : row.priority === 'high'
                              ? 'bg-amber-500/10 text-amber-600'
                              : 'bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300'
                          }`}
                        >
                          {row.priority || 'medium'}
                        </span>
                      </td>
                      <td className="p-2 text-surface-500 truncate max-w-[120px]">
                        {row.assigneeEmail || '-'}
                      </td>
                      <td className="p-2 font-mono text-[10px] text-surface-500">
                        {row.dueDate || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedRows.length > PREVIEW_ROW_LIMIT && (
              <p className="text-[11px] text-surface-500">
                Showing the first {PREVIEW_ROW_LIMIT} of {parsedRows.length} rows. All valid rows are imported.
              </p>
            )}
          </div>
        )}

        {/* Alerts */}
        {errorMsg && (
          <div className="p-3 bg-rose-50 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900 text-rose-600 dark:text-rose-400 flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-900 text-emerald-600 dark:text-emerald-400 flex items-center space-x-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Modal Footer Controls */}
        <div className="pt-3 border-t border-surface-200 dark:border-surface-800 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl font-bold"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleImportSubmit}
            disabled={isSubmitting || validCount === 0 || !selectedProjectId}
            className="px-5 py-2 rounded-xl bg-pulse-gradient text-white font-extrabold shadow-pulse hover:opacity-95 disabled:opacity-50 flex items-center space-x-1.5 transition-all"
          >
            <Upload className="w-3.5 h-3.5" />
            <span>
              {isSubmitting
                ? 'Importing...'
                : `Import ${validCount} Task${validCount === 1 ? '' : 's'}`}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
